import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import { Seo, breadcrumbSchema, professionalServiceSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionTag } from "@/components/SectionTag";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { OfferCard } from "@/components/OfferCard";
import { FaqAccordion } from "@/components/FaqAccordion";
import { CTABlock } from "@/components/CTABlock";
import { Hero } from "@/pages/home/Hero";
import { Positioning } from "@/pages/home/Positioning";
import { PainPoints } from "@/pages/home/PainPoints";
import { WhyMe } from "@/pages/home/WhyMe";
import { Method } from "@/pages/home/Method";
import { AiSection } from "@/pages/home/AiSection";
import { OFFERS } from "@/data/offers";
import { OFFERS_EN } from "@/data/offers.en";
import { ARTICLES } from "@/data/articles";
import { ARTICLES_EN } from "@/data/articles.en";
import { FAQS } from "@/data/faq";
import { FAQS_EN } from "@/data/faq.en";
import { useLang } from "@/i18n/LangContext";

// Témoignages masqués en attendant les premiers retours clients — passer à true pour réafficher la section
const SHOW_TESTIMONIALS = false;

export default function Home() {
  const { t, l, lang } = useLang();
  const offers = lang === "en" ? OFFERS_EN : OFFERS;
  const articles = lang === "en" ? ARTICLES_EN : ARTICLES;
  const faqs = lang === "en" ? FAQS_EN : FAQS;
  const formatDate = (d) =>
    new Date(d).toLocaleDateString(t.meta.dateLocale, { day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      <Seo
        title={t.home.seo.title}
        description={t.home.seo.description}
        path="/"
        schemas={[
          professionalServiceSchema(lang),
          breadcrumbSchema([{ name: t.common.home, path: "/" }]),
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.slice(0, 6).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a.replace(/\[\[[^\]|]+\|([^\]]+)\]\]/g, "$1") },
            })),
          },
        ]}
      />

      <Hero />
      <EditorialMarquee />
      <Positioning />
      <PainPoints />

      <section className="px-5 md:px-10 py-24 md:py-32 bg-white" data-testid="offers">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-2xl">
              <SectionTag>{t.home.offers.tag}</SectionTag>
              <Reveal delay={0.1}>
                <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-balance">
                  {t.home.offers.h2}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to={l("/services")}
                data-testid="offers-see-all"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-terracotta-deep"
              >
                {t.home.offers.seeAll}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 md:mt-20 grid gap-6 md:grid-cols-3">
            {offers.map((offer, i) => (
              <OfferCard key={offer.slug} offer={offer} index={i} />
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12">
            <p className="mx-auto max-w-3xl text-center text-sm md:text-base leading-relaxed text-plum/70">
              {t.home.offers.customA}{" "}
              <Link to={l("/contact")} data-testid="offers-custom-link" className="font-semibold text-terracotta-deep underline decoration-terracotta/40 underline-offset-4 hover:decoration-terracotta">
                {t.home.offers.customLink}
              </Link>{" "}
              {t.home.offers.customB}
            </p>
          </Reveal>
        </div>
      </section>

      <WhyMe />
      <EditorialMarquee dark />
      <Method />
      <AiSection />

      {SHOW_TESTIMONIALS && (
      <section className="px-5 md:px-10 py-24 md:py-32" data-testid="testimonials">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <SectionTag>{t.home.testimonials.tag}</SectionTag>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-balance">
                {t.home.testimonials.h2}
              </h2>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="flex h-full min-h-56 flex-col items-center justify-center rounded-3xl border-2 border-dashed border-terracotta/30 bg-white/50 p-8 text-center">
                  <Quote className="w-7 h-7 text-terracotta/50" aria-hidden="true" />
                  <p className="mt-4 text-sm font-medium text-plum/50">
                    {t.home.testimonials.placeholder}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      <section className="px-5 md:px-10 py-24 md:py-32 bg-white" data-testid="latest-articles">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-2xl">
              <SectionTag>{t.home.articles.tag}</SectionTag>
              <Reveal delay={0.1}>
                <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-balance">
                  {t.home.articles.h2}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to={l("/blog")}
                data-testid="articles-see-all"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-terracotta-deep"
              >
                {t.home.articles.seeAll}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {articles.slice(0, 3).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.1} className="h-full">
                <Link
                  to={l(`/blog/${a.frSlug || a.slug}`)}
                  data-testid={`home-article-${a.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-ink/5 bg-cream/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_28px_50px_-15px_rgba(17,14,13,0.12)]"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.imageAlt}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-terracotta-deep">
                      {a.category}
                    </span>
                    <h3 className="mt-3 font-display text-lg font-bold tracking-tight leading-snug flex-1">
                      {a.title}
                    </h3>
                    <p className="mt-4 text-xs text-plum/50">
                      {formatDate(a.date)} · {a.readTime} {t.common.readSuffix}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 py-24 md:py-32" data-testid="home-faq">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <SectionTag>{t.home.faq.tag}</SectionTag>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-balance">
                {t.home.faq.h2}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="mt-14">
            <FaqAccordion items={faqs.slice(0, 6)} testidPrefix="home-faq" />
          </Reveal>
          <Reveal delay={0.2} className="mt-10 text-center">
            <Link
              to={l("/faq")}
              data-testid="home-faq-see-all"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-terracotta-deep"
            >
              {t.home.faq.seeAll}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABlock />
    </>
  );
}
